import { Component, inject, OnDestroy, ViewChild } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { MatSelect } from '@angular/material/select';
import { ActivatedRoute, Router } from '@angular/router';
import { Subscription } from 'rxjs';
import { Azienda, AziendaService } from 'src/app/services/azienda/azienda.service';
import { TemaService } from 'src/app/services/tema/tema.service';
import { UtenteIncaricatoService } from 'src/app/services/utente/utente-incaricato.service';
import { InfoUtente } from 'src/app/services/utente/utente-web.service';
import { UtenteService } from 'src/app/services/utente/utente.service';
import { UtilityService } from 'src/app/services/utility/utility.service';
import { DialogPdfViewerComponent } from '../dialog-pdf-viewer/dialog-pdf-viewer.component';

/**
 * Struttura del menu sidenav
 * la struttura può avere anche sottovoci(children)
 */
interface VociMenu {
  sezione: string;
  fullPath: string;
  icon: string;
  sottoSezione?: VociMenu[];
  disabled?: boolean;
  mostrato?: boolean;
  isExpanded?: boolean;
  onClickMenu?: () => void; // Funzione da eseguire al click
}

@Component({
  selector: 'app-home',
  templateUrl: './home.component.html',
  styleUrls: ['./home.component.scss'],
  standalone: false,
})
export class HomeComponent implements OnDestroy {
  @ViewChild(MatSelect) matSelect!: MatSelect;

  public isSidenavMini = false;
  public vociMenu: VociMenu[] = [];
  public salvoMenu: VociMenu[] = [];

  public infoUtente: InfoUtente | undefined;

  public logoPath: string = '';

  public isActive: boolean = false;

  private _subAziendaCorrente: Subscription | undefined;
  private _isUtenteGo: boolean = false;

  public azienda: Azienda | undefined;
  public arrayAziende: Azienda[] = [];

  // Lo inietto qui solo per "attivarlo". Cosi sente il cambio del azienda e prende il suo responsabile
  private readonly _licenzaPreloadUtenteIncaricato = inject(UtenteIncaricatoService);

  private get _isConfiguratore(): boolean {
    return this.infoUtente?.utente?.ruolo?.roleName === 'configuratore';
  }

  constructor(
    public dialog: MatDialog,
    public router: Router,
    public activatedRoute: ActivatedRoute,
    private readonly aziendaService: AziendaService,
    private readonly utilityService: UtilityService,
    private readonly utenteService: UtenteService,
    private readonly temaService: TemaService,
  ) {
    this._init();
  }

  private _init() {
    this.temaService.temaUpdated.subscribe((tipoInstallazione) => {
      this.logoPath = this.temaService.logoPath;
      if (tipoInstallazione === 'SOSTENIBILE_GO') {
        this._isUtenteGo = true;
      } else {
        this._isUtenteGo = false;
      }
      this._getMenu(this.azienda?.id);
    });

    this.infoUtente = this.utenteService.infoUtente;

    this._subAziendaCorrente = this.aziendaService.aziendaCorrente.subscribe(
      (azienda: Azienda | undefined) => {
        this.azienda = azienda;
        this._getMenu(azienda?.id);
      },
    );

    this.aziendaService.listaAziende.subscribe((listaAziende) => {
      this.arrayAziende = listaAziende || [];

      this.aziendaService.azienda.then((val) => {
        this.azienda = val;
      });
    });

    if (this.azienda?.id) {
      this.aziendaService.setSoglia(this.azienda?.id);
    }
  }

  ngOnDestroy(): void {
    this._subAziendaCorrente?.unsubscribe();
  }

  hasChild = (_: number, node: VociMenu) => !!node.sottoSezione && node.sottoSezione.length > 0;

  azionaMenu(menu: VociMenu) {
    if (menu?.disabled === true) {
      this.utilityService.openDialog({
        titolo: 'Attenzione',
        descrizione: 'Seleziona un azienda',
        bottoni: [
          {
            nome_btn: 'Ok',
            handler: () => {
              this.matSelect.open();
            },
          },
        ],
      });
    } else if (menu.onClickMenu) {
      menu.onClickMenu();
    } else if (menu.fullPath) {
      this.router.navigate([menu.fullPath]);
    }
  }

  public compareAzienda(azienda1: Azienda, azienda2: Azienda): boolean {
    return azienda1?.id === azienda2?.id; // Sostituisci "id" con la proprietà univoca degli ambiti
  }

  public cambioAzienda(azienda: Azienda) {
    this.aziendaService.azienda = azienda;
  }

  public logout() {
    this.utenteService.logout();
  }

  isRouteActive(fullPath: string): boolean {
    return this.router.isActive(fullPath, {
      paths: 'subset',
      matrixParams: 'ignored',
      queryParams: 'ignored',
      fragment: 'ignored',
    });
  }

  private _getMenu(idAzienda?: string) {
    this.vociMenu = [
      {
        sezione: 'Dashboard',
        fullPath: '/home/dashboard',
        icon: 'dashboard',
        sottoSezione: [],
        mostrato: true,
      },
      {
        sezione: 'Assessment di Sostenibilità',
        fullPath: '/home/assessment',
        icon: 'star_border',
        sottoSezione: [],
        disabled: !idAzienda,
        mostrato: !this._isUtenteGo && this.utenteService.isPermessoAttivo('READ_AUTOVALUTAZIONI'),
      },
      {
        sezione: 'Policy di Sostenibilità',
        fullPath: '/home/policy-sostenibilita',
        icon: 'policy',
        sottoSezione: [],
        disabled: !idAzienda,
        mostrato: !this._isUtenteGo && this.utenteService.isPermessoAttivo('READ_POLICY'),
      },
      {
        sezione: 'Piano di Sostenibilità',
        fullPath: '/home/piano-sostenibilita',
        icon: 'event_note',
        sottoSezione: [],
        disabled: !idAzienda,
        mostrato: !this._isUtenteGo && this.utenteService.isPermessoAttivo('READ_PIANO'),
      },
      {
        sezione: 'Analisi Materialità',
        fullPath: '/home/analisi-materialità',
        icon: 'text_snippet',
        mostrato:
          !this._isUtenteGo &&
          (this.utenteService.isPermessoAttivo('READ_STAKEHOLDERS') ||
            this.utenteService.isPermessoAttivo('READ_MATERIALITA') ||
            this.utenteService.isPermessoAttivo('READ_DOPPIA_MATERIALITA')),
        isExpanded: false,
        sottoSezione: [
          {
            sezione: 'Stakeholder',
            fullPath: '/home/stakeholder',
            icon: '•',
            disabled: !idAzienda,
            mostrato: this.utenteService.isPermessoAttivo('READ_STAKEHOLDERS'),
          },
          {
            sezione: 'Materialità',
            fullPath: '/home/materialita',
            icon: '•',
            disabled: !idAzienda,
            mostrato: this.utenteService.isPermessoAttivo('READ_MATERIALITA'),
          },
          {
            sezione: 'Doppia Materialità',
            fullPath: '/home/doppia-materialita',
            icon: '•',
            disabled: !idAzienda,
            mostrato: this.utenteService.isPermessoAttivo('READ_DOPPIA_MATERIALITA'),
          },
        ],
      },
      {
        sezione: 'Sintesi di Sostenibilità',
        fullPath: '/home/sostenibilita',
        icon: 'spa',
        disabled: !idAzienda,
        mostrato: !this._isUtenteGo && this.utenteService.isPermessoAttivo('READ_SINTESI'),
      },
      {
        sezione: this._isUtenteGo ? 'Report Tematico' : 'Report di Sostenibilità',
        fullPath: '/home/report-di-sostenibilità',
        icon: 'balance',
        disabled: !idAzienda,
        mostrato: !this._isUtenteGo && this.utenteService.isPermessoAttivo('READ_BILANCIO'),
      },
      {
        sezione: 'Report VSME',
        fullPath: '/home/report-vsme',
        icon: 'energy_savings_leaf',
        disabled: !idAzienda,
        mostrato: this.utenteService.isPermessoAttivo('READ_VSME'),
      },
      {
        sezione: 'Aziende',
        fullPath: '/home/aziende',
        icon: 'apartment',
        mostrato:
          this.utenteService.isPermessoAttivo('READ_AZIENDA') ||
          this.utenteService.isPermessoAttivo('READ_FIGURA_AZ'),
        isExpanded: false,
        sottoSezione: [
          {
            sezione: 'Aziende',
            fullPath: '/home/aziende',
            icon: '•',
            mostrato: this.utenteService.isPermessoAttivo('READ_AZIENDA'),
          },
          {
            sezione: 'Figure Aziendali',
            fullPath: '/home/figure-aziendali',
            icon: '•',
            disabled: !idAzienda,
            mostrato: this.utenteService.isPermessoAttivo('READ_FIGURA_AZ'),
          },
          {
            sezione: 'Comitati',
            fullPath: '/home/comitati',
            icon: '•',
            disabled: !idAzienda,
            mostrato: this.utenteService.isPermessoAttivo('READ_COMITATI') && !this._isUtenteGo,
          },
        ],
      },
      {
        sezione: 'Utenti',
        fullPath: '/home/utenti',
        icon: 'group',
        sottoSezione: [],
        mostrato: this.utenteService.isPermessoAttivo('READ_UTENTI'),
      },
      {
        sezione: 'Documenti',
        fullPath: 'home/bucket-documenti',
        icon: 'folder_copy',
        sottoSezione: [],
        mostrato: this.utenteService.isPermessoAttivo('READ_DOCUMENTI'),
      },
      {
        sezione: this._isUtenteGo ? 'Manuale Operativo' : 'Materiale Informativo',
        fullPath: '/home/',
        icon: 'menu_book',
        sottoSezione: this._isUtenteGo
          ? []
          : [
              {
                sezione: 'Manuale Operativo',
                fullPath: '/home/',
                icon: '•',
                mostrato: true,
                onClickMenu: () => this.apriPDF(),
              },
              {
                sezione: 'Guida Multimediale',
                fullPath: '/home/',
                icon: '•',
                mostrato: true,
                onClickMenu: () => this.apriLinkVideo(),
              },
            ],
        mostrato: true,
        onClickMenu: () => this.apriPDF(),
      },
      {
        sezione: 'Configurazioni',
        fullPath: '/home/configurazioni',
        icon: 'settings_application',
        mostrato: this._isConfiguratore,
        isExpanded: false,
        sottoSezione: [
          {
            sezione: 'Ambiti',
            fullPath: '/home/configurazioni-ambiti',
            icon: '•',
            mostrato: this._isConfiguratore,
          },
          {
            sezione: 'Tematiche',
            fullPath: '/home/configurazioni-tematiche',
            icon: '•',
            mostrato: this._isConfiguratore,
          },
          {
            sezione: 'ESRS',
            fullPath: '/home/configurazioni-esrs',
            icon: '•',
            mostrato: this._isConfiguratore,
          },
          {
            sezione: 'Impatti',
            fullPath: '/home/configurazioni-impatti',
            icon: '•',
            mostrato: this._isConfiguratore,
          },
          {
            sezione: 'Impegni',
            fullPath: '/home/configurazioni-impegni',
            icon: '•',
            mostrato: this._isConfiguratore,
          },
          {
            sezione: 'Azioni e Buone Pratiche',
            fullPath: '/home/configurazioni-azione-e-buona-pratica',
            icon: '•',
            mostrato: this._isConfiguratore,
          },
          {
            sezione: 'KPI',
            fullPath: '/home/configurazioni-KPI',
            icon: '•',
            mostrato: this._isConfiguratore,
          },
          {
            sezione: 'Standard di Rendicontazione',
            fullPath: '/home/configurazioni-standard-rendicontazione',
            icon: '•',
            mostrato: this._isConfiguratore,
          },
          {
            sezione: 'Assessment di Sostenibilità',
            fullPath: '/home/configurazioni-autovalutazioni',
            icon: '•',
            mostrato: this._isConfiguratore,
          },
          {
            sezione: 'Categorie Stakeholder',
            fullPath: '/home/configurazioni-cate-stakeholder',
            icon: '•',
            mostrato: this._isConfiguratore,
          },
          {
            sezione: 'Guide Scrittura Impatto',
            fullPath: '/home/configurazioni-scrittura-impatto',
            icon: '•',
            mostrato: this._isConfiguratore,
          },
          {
            sezione: 'Informativa Aziendale',
            fullPath: '/home/configurazioni-informativa-aziendale',
            icon: '•',
            mostrato: this._isConfiguratore,
          },
          {
            sezione: 'Settori',
            fullPath: '/home/configurazioni-settori',
            icon: '•',
            mostrato: this._isConfiguratore,
          },
          {
            sezione: 'Unità di misura',
            fullPath: '/home/configurazioni-unita-di-misura',
            icon: '•',
            mostrato: this._isConfiguratore,
          },
          {
            sezione: 'Certificazioni',
            fullPath: '/home/configurazioni-certificazioni',
            icon: '•',
            mostrato: this._isConfiguratore,
          },
          {
            sezione: 'Comitati',
            fullPath: '/home/configurazioni-comitati',
            icon: '•',
            mostrato: this._isConfiguratore,
          },
          /*   {
              sezione: 'Destinatario di Applicazione Policy',
              fullPath: '/home/configurazioni-perimetro-app-policy',
              icon: '•',
              mostrato: this._isConfiguratore
            }, */
          {
            sezione: 'Destinatario di Applicazione Policy',
            fullPath: '/home/configurazioni-destinatari-policy',
            icon: '•',
            mostrato: this._isConfiguratore,
          },
          {
            sezione: 'Linee guida stampe e Mail',
            fullPath: '/home/configurazioni-linee-guida-stampe',
            icon: '•',
            mostrato: this._isConfiguratore,
          },
          {
            sezione: 'Obiettivi',
            fullPath: '/home/configurazioni-obiettivi-piano',
            icon: '•',
            mostrato: this._isConfiguratore,
          },
          {
            sezione: 'Obiettivi Aziendali',
            fullPath: '/home/configurazioni-obiettivi-aziendali-piano',
            icon: '•',
            mostrato: this._isConfiguratore,
          },
          {
            sezione: 'Modelli Sviluppo Piano',
            fullPath: '/home/configurazioni-modelli-sviluppo-piano',
            icon: '•',
            mostrato: this._isConfiguratore,
          },
          {
            sezione: 'Prompt Generali',
            fullPath: '/home/configurazioni-general-prompt',
            icon: '•',
            mostrato: this._isConfiguratore,
          },
        ],
      },
    ]
      .map((voce) => {
        (voce.sottoSezione as VociMenu[]) = ((voce.sottoSezione as VociMenu[]) || []).filter(
          (sottoVoce: VociMenu) => sottoVoce.mostrato,
        );
        return voce;
      })
      .filter((voce) => voce.mostrato);
  }

  /**
   * Apertura sotto menu
   * @param index
   * @param voceMenu
   */
  toggleSubMenu(index: number, voceMenu: VociMenu): void {
    this.vociMenu[index].isExpanded = !this.vociMenu[index].isExpanded;

    // Se il menu e chiuso (quindi in modalità mini) e clicco su un menu con sotto menu, allora lo apro
    if (voceMenu?.sottoSezione?.length && this.isSidenavMini) {
      this.isSidenavMini = !this.isSidenavMini;
    }
  }

  /**
   * on off menu laterale
   */
  toolbarMenuButton() {
    // Se il menu è aperto mi salvo l'impostazione del menu
    if (!this.isSidenavMini) {
      this.salvoMenu = JSON.parse(JSON.stringify(this.vociMenu));
    }

    this.isSidenavMini = !this.isSidenavMini;

    if (this.isSidenavMini) {
      this.vociMenu.forEach((voceMenu: VociMenu) => {
        voceMenu.isExpanded = false;
      });
    } else {
      this.vociMenu = JSON.parse(JSON.stringify(this.salvoMenu));
    }
  }

  apriPDF() {
    const pdfManualeGo = 'assets/pdf/Manuale operativo Sostenibile.go_REV3.pdf';
    const pdfManualeCloud = 'assets/pdf/V.2 Manuale operativo Sostenibile.cloud.pdf';

    const dialogPDFViewer = this.dialog.open(DialogPdfViewerComponent, {
      data: {
        pdfPath: this._isUtenteGo ? pdfManualeGo : pdfManualeCloud,
      },
      panelClass: ['dialog-container'],
      disableClose: false,
      width: '80%',
      maxHeight: '95%',
      autoFocus: false,
    });

    dialogPDFViewer.afterClosed().subscribe((result) => {});
  }

  apriLinkVideo(): void {
    const youtubeUrl =
      'https://www.youtube.com/watch?v=EeuS1k0Ps-s&list=PLB92Bdimm1TMoQ39jt_nqkAH0DTOUirWZ'; // Inserisci il link corretto
    window.open(youtubeUrl, '_blank');
  }

  goToDashboard() {
    this.router.navigate(['dashboard'], {
      relativeTo: this.activatedRoute,
    });
  }
}
